import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { formatDate, formatNumber, titleCase } from '../../lib/format'
import { useAuth } from '../../lib/auth'
import { portalFor } from '../../lib/roles'
import type { Expense, ListResponse, PersonOption } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Select, Skeleton,
} from '../../components/ui'
import { Wizard } from '../../components/Wizard'
import { RangeChips, WeeklyBarChart, bucketWeekly } from '../../components/charts'
import { CloseIcon, PlusIcon } from '../../components/icons'

const CATEGORIES = [
  'fuel', 'driver_salary', 'staff_salary', 'food', 'maintenance',
  'hire_fee', 'permit', 'transport', 'supplies', 'other',
]

export default function Expenses() {
  const qc = useQueryClient()
  const { user } = useAuth()
  // super_admin belongs to no single cooperative, so "log an expense" has no
  // org to attach it to here - this page is platform-wide oversight for
  // super_admin, not a place to log new spend on another org's behalf.
  const isAdmin = portalFor(user) === 'admin'
  const [showForm, setShowForm] = useState(false)
  // Summary = totals, per-category breakdown and the weekly spend trend;
  // Table = the raw ledger. Same Summary|Table split as other report pages.
  const [view, setView] = useState<'summary' | 'table'>('summary')
  const [weeks, setWeeks] = useState(8)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['leader', 'expenses'],
    queryFn: () => api.get<ListResponse<Expense> & { categories: string[] }>('/leader/expenses'),
  })
  const peopleQuery = useQuery({
    queryKey: ['leader', 'people'],
    queryFn: () => api.get<{ ok: boolean; items: PersonOption[] }>('/leader/people'),
    enabled: showForm,
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/leader/expenses', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leader', 'expenses'] })
      setShowForm(false)
    },
  })

  // super_admin-only moderation action - see PATCH /leader/expenses/:id,
  // which only accepts is_private from a super_admin caller.
  const togglePrivacy = useMutation({
    mutationFn: ({ id, isPrivate }: { id: string; isPrivate: boolean }) =>
      api.patch(`/leader/expenses/${id}`, { is_private: isPrivate }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['leader', 'expenses'] }),
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const personUid = String(form.get('person_uid') ?? '')
    const person = peopleQuery.data?.items.find((p) => p.uid === personUid)
    create.mutate({
      category: String(form.get('category') ?? 'other'),
      amount: Number(form.get('amount') ?? 0),
      description: String(form.get('description') ?? '').trim(),
      person_uid: personUid || undefined,
      person_name: person?.name,
      payee: String(form.get('payee') ?? '').trim(),
    })
  }

  const items = data?.items ?? []
  const total = items.reduce((sum, e) => sum + (e.amount ?? 0), 0)
  const byCategory = (() => {
    const totals = new Map<string, number>()
    for (const e of items) totals.set(e.category || 'other', (totals.get(e.category || 'other') ?? 0) + (e.amount ?? 0))
    return [...totals.entries()].sort((a, b) => b[1] - a[1])
  })()

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Expenses</h1>
          <p className="page-head__sub">
            {isAdmin
              ? 'Operational costs across every organisation — fuel, salaries, hire fees and more.'
              : 'Operational costs — fuel, salaries, hire fees and more.'}
          </p>
        </div>
        {!isAdmin && (
          <>
            <Button className="fab-trigger__inline" icon={<PlusIcon />} onClick={() => setShowForm((s) => !s)}>
              {showForm ? 'Cancel' : 'Log expense'}
            </Button>
            <button
              type="button" className="fab"
              onClick={() => setShowForm((s) => !s)}
              aria-label={showForm ? 'Cancel' : 'Log expense'}
            >
              {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
            </button>
          </>
        )}
      </div>

      {!isAdmin && showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="New expense" />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                {create.isError && (
                  <Alert tone="danger">
                    {create.error instanceof ApiError ? create.error.message : 'Could not log that expense.'}
                  </Alert>
                )}
                {/* PWA: 2 guided steps; desktop stacked, unchanged. */}
                <Wizard
                  steps={[
                    { title: 'What & how much', content: (
                      <div className="stack">
                        <div className="grid grid--2">
                          <Select label="Category" name="category" defaultValue="fuel">
                            {CATEGORIES.map((c) => <option key={c} value={c}>{titleCase(c)}</option>)}
                          </Select>
                          <Input label="Amount (ZMW)" name="amount" type="number" step="any" min="0" required />
                        </div>
                        <Input label="Description" name="description" />
                      </div>
                    ) },
                    { title: 'Who', content: (
                      <div className="grid grid--2">
                        <Select label="Attach to a person (optional)" name="person_uid" defaultValue="">
                          <option value="">None</option>
                          {(peopleQuery.data?.items ?? []).map((p) => (
                            <option key={p.uid} value={p.uid}>{p.name || p.email}</option>
                          ))}
                        </Select>
                        <Input label="Payee (optional)" name="payee" />
                      </div>
                    ) },
                  ]}
                  footer={(
                    <div className="row">
                      <Button type="submit" loading={create.isPending} confirm={{ title: "Record this expense?" }}>Log expense</Button>
                    </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
        <Button variant={view === 'summary' ? 'primary' : 'secondary'} size="sm" onClick={() => setView('summary')}>
          Summary
        </Button>
        <Button variant={view === 'table' ? 'primary' : 'secondary'} size="sm" onClick={() => setView('table')}>
          Table
        </Button>
      </div>

      {view === 'summary' && !isLoading && !isError && (
        <div className="stack" style={{ gap: 'var(--sp-5)', marginBottom: 'var(--sp-6)' }}>
          <div className="grid grid--tiles-2">
            <Card><div className="stat">
              <div className="stat__top"><span className="stat__label">Total spend</span></div>
              <div className="stat__value">K{formatNumber(total, 0)}</div>
              <div className="stat__meta">{items.length} record{items.length === 1 ? '' : 's'}</div>
            </div></Card>
            <Card><div className="stat">
              <div className="stat__top"><span className="stat__label">Top category</span></div>
              <div className="stat__value">{byCategory[0] ? titleCase(byCategory[0][0]) : '—'}</div>
              <div className="stat__meta">{byCategory[0] ? `K${formatNumber(byCategory[0][1], 0)}` : 'No spend yet'}</div>
            </div></Card>
          </div>
          <Card>
            <CardHeader title="Spend per week" sub={`Expenses logged (ZMW), last ${weeks} weeks`} />
            <CardBody>
              <RangeChips value={weeks} onChange={setWeeks} />
              {(() => {
                const s = bucketWeekly(items, (e) => e.spent_at,
                  (e) => Number(e.amount) || 0, weeks)
                return <WeeklyBarChart weeks={s.weeks} values={s.values} />
              })()}
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="By category" sub="Sorted by total spend" />
            <CardBody>
              {byCategory.length === 0 ? <EmptyState title="No expenses logged" /> : (
                <div className="stack" style={{ gap: 'var(--sp-3)' }}>
                  {byCategory.map(([cat, amt]) => (
                    <div key={cat}>
                      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 'var(--text-sm)' }}>{titleCase(cat)}</span>
                        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 700 }}>K{formatNumber(amt, 0)}</span>
                      </div>
                      <div style={{ height: 8, background: 'var(--accent-soft)', borderRadius: 4 }}>
                        <div style={{
                          height: '100%', borderRadius: 4, background: 'var(--accent)',
                          width: `${Math.max(4, Math.round((amt / (byCategory[0]?.[1] || 1)) * 100))}%`,
                        }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      )}

      {view === 'table' && (
      <Card>
        <CardHeader
          title="Expenses"
          sub={items.length ? `${items.length} records · ZMW ${formatNumber(total, 2)} total` : undefined}
        />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack"><Skeleton h={20} /><Skeleton h={20} /></div>
          ) : items.length === 0 ? (
            <EmptyState title="No expenses logged" body="Costs recorded by your team will appear here." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th><th>Category</th><th>Description</th><th className="table__num">Amount</th>
                    {isAdmin && <th />}
                  </tr>
                </thead>
                <tbody>
                  {items.map((e) => (
                    <tr key={e.id}>
                      <td>{formatDate(e.spent_at)}</td>
                      <td>{titleCase(e.category)}</td>
                      <td>{e.description || e.payee || e.person_name || '—'}</td>
                      <td className="table__num">{e.currency || 'ZMW'} {formatNumber(e.amount, 2)}</td>
                      {isAdmin && (
                        <td style={{ textAlign: 'right' }}>
                          <Button
                            size="sm" variant="ghost"
                            loading={togglePrivacy.isPending && togglePrivacy.variables?.id === e.id}
                            onClick={() => e.id && togglePrivacy.mutate({ id: e.id, isPrivate: !e.is_private })}
                          >
                            {e.is_private ? 'Make public' : 'Mark private'}
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      )}
    </>
  )
}
